/**
 * AINutritionGoalsModal Component
 * 
 * Presentational modal for SmartGoals.
 * EXACT same UI as original App.js - no changes to styling or behavior.
 * 
 * All state and handlers are passed in as props.
 */

import React from 'react';
import CustomTargetsEditor from './CustomTargetsEditor';

// Weight conversion helper
const kgToLbs = (kg) => {
  if (!kg || isNaN(kg)) return null;
  return (kg * 2.20462).toFixed(1);
};

const AINutritionGoalsModal = ({
  // State
  aiNutritionGoals,
  miqIntakeOptions,
  userProfile,
  metabolicProfile,
  todayMeals,
  userId,
  dailyCheckinContribution,
  checkedInToday,
  BACKEND_URL,
  aiMiq, // MIQ data for actual confidence
  userTier, // User's subscription tier for feature gating
  
  // Setters & Handlers
  setShowAiNutritionGoals,
  showUpgrade,
  loadAiMiq,
  loadMiqIntakeOptions,
  loadAiNutritionGoals,
  setAiNutritionGoals,
  setShowMealPlanner,
  setShowDailyCheckin,
  showCoachToast,  // For goals confirmation toast
  setCurrentView,  // For "View Targets" CTA navigation
  
  // MIQ Intake Form Component (passed as prop to avoid import issues)
  MiqIntakeForm
}) => {
  // Edit mode state - allows editing saved SmartGoals
  const [isEditing, setIsEditing] = React.useState(false);
  
  // Show form if: not locked AND (not complete OR editing mode)
  const showForm = !aiNutritionGoals?.locked && (!aiNutritionGoals?.intake_complete || isEditing);
  // Show display if: not locked AND complete AND not editing
  const showDisplay = !aiNutritionGoals?.locked && aiNutritionGoals?.intake_complete && !isEditing;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--bg-primary)',
        borderRadius: '20px',
        maxWidth: '420px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '24px'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            🧠 SmartGoals
            <span style={{ display: 'block', fontSize: '11px', fontWeight: '400', color: 'var(--text-secondary)', marginTop: '2px' }}>
              powered by MetaBolicIQ (MIQ)
            </span>
          </h2>
          <button 
            onClick={() => {
              setIsEditing(false);
              setShowAiNutritionGoals(false);
            }}
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary)' }}
          >
            ✕
          </button>
        </div>
        
        {/* Locked State */}
        {aiNutritionGoals?.locked && (
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🔒</span>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>
              Part of MIQ
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              {aiNutritionGoals.description}
            </p>
            <ul style={{ textAlign: 'left', margin: '0 0 20px 0', padding: '0 0 0 20px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              {aiNutritionGoals.features?.map((f, i) => (
                <li key={i} style={{ marginBottom: '6px' }}>{f}</li>
              ))}
            </ul>
            <button
              onClick={() => {
                setShowAiNutritionGoals(false);
                showUpgrade('nutrition_goals');
              }}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '14px',
                borderRadius: '10px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Start MIQ Calibration — $9.99/mo
            </button>
          </div>
        )}
        
        {/* MIQ Intake Form (Premium users) - Now supports editing saved data */}
        {showForm && miqIntakeOptions && MiqIntakeForm && (
          <MiqIntakeForm 
            options={miqIntakeOptions}
            userId={userId}
            BACKEND_URL={BACKEND_URL}
            userProfile={userProfile}
            savedIntakeData={aiNutritionGoals?.intake_data}
            onComplete={async (data) => {
              // Exit edit mode
              setIsEditing(false);
              // Immediately update state with the returned data (includes intake_complete: true)
              setAiNutritionGoals(data);
              
              // IMPORTANT: Fetch fresh targets BEFORE closing modal (guards check modal open state)
              try {
                console.log('[SmartGoals] Fetching fresh targets from:', `${BACKEND_URL}/api/ai-nutrition-goals/${userId}`);
                const response = await fetch(`${BACKEND_URL}/api/ai-nutrition-goals/${userId}`);
                if (response.ok) {
                  const freshData = await response.json();
                  console.log('[SmartGoals] Fresh data received:', freshData?.nutrition_goals?.daily_targets);
                  setAiNutritionGoals(freshData);
                } else {
                  console.error('[SmartGoals] Failed to fetch fresh targets:', response.status);
                }
              } catch (err) {
                console.error('[SmartGoals] Error refreshing SmartGoals:', err);
              }
              
              // Reload MIQ to reflect updated confidence
              loadAiMiq(userId);
              
              // Show confirmation toast (auto-dismisses after 4 seconds)
              if (showCoachToast) {
                showCoachToast(
                  aiNutritionGoals?.intake_complete 
                    ? "SmartGoals updated. Your targets have been recalculated."
                    : "SmartGoals established. Your calorie targets and macros now reflect intelligent personalization.",
                  "View Targets",  // CTA button label
                  () => setCurrentView && setCurrentView('home'),  // Navigate to home to see targets
                  '✅',
                  "MIQ will continue refining these targets as you log meals."  // Secondary message
                );
              }
              // Close the modal AFTER fetching fresh data
              setShowAiNutritionGoals(false);
            }}
            onClose={() => {
              setIsEditing(false);
              setShowAiNutritionGoals(false);
            }}
          />
        )}
        
        {/* Loading MIQ Intake Options */}
        {!aiNutritionGoals?.locked && !aiNutritionGoals?.intake_complete && !miqIntakeOptions && aiNutritionGoals && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>⏳</span>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>Loading SmartGoals form...</p>
            <button
              onClick={() => loadMiqIntakeOptions()}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Load Form
            </button>
          </div>
        )}
        
        {/* Loading/No Data State - Only show when we have neither aiNutritionGoals NOR miqIntakeOptions */}
        {/* If we have miqIntakeOptions, the form is shown above - don't show loading */}
        {!aiNutritionGoals && !miqIntakeOptions && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>⏳</span>
            <p style={{ color: 'var(--text-secondary)' }}>Loading SmartGoals...</p>
            <button
              onClick={() => {
                loadMiqIntakeOptions();
                loadAiNutritionGoals(userId);
              }}
              style={{
                marginTop: '16px',
                padding: '10px 20px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Retry Loading
            </button>
          </div>
        )}
        
        {/* Nutrition Goals Display (after intake complete) */}
        {showDisplay && (
          <NutritionGoalsDisplay 
            aiNutritionGoals={aiNutritionGoals}
            userProfile={userProfile}
            metabolicProfile={metabolicProfile}
            todayMeals={todayMeals}
            dailyCheckinContribution={dailyCheckinContribution}
            checkedInToday={checkedInToday}
            setShowAiNutritionGoals={setShowAiNutritionGoals}
            setShowMealPlanner={setShowMealPlanner}
            setShowDailyCheckin={setShowDailyCheckin}
            setAiNutritionGoals={setAiNutritionGoals}
            userId={userId}
            loadAiNutritionGoals={loadAiNutritionGoals}
            BACKEND_URL={BACKEND_URL}
            aiMiq={aiMiq}
            onEditSmartGoals={() => setIsEditing(true)}
          />
        )}
      </div>
    </div>
  );
};

/**
 * NutritionGoalsDisplay - Shows goals after intake is complete
 * Inner component to handle the complex display logic
 */
const NutritionGoalsDisplay = ({
  aiNutritionGoals,
  userProfile,
  metabolicProfile,
  todayMeals,
  dailyCheckinContribution,
  checkedInToday,
  setShowAiNutritionGoals,
  setShowMealPlanner,
  setShowDailyCheckin,
  // New props for retake functionality
  onRetakeAssessment,
  setAiNutritionGoals,
  // New props for custom targets
  userId,
  loadAiNutritionGoals,
  BACKEND_URL,
  aiMiq, // MIQ data for actual confidence
  onEditSmartGoals // New prop for edit functionality
}) => {
  const [showRetakeConfirm, setShowRetakeConfirm] = React.useState(false);
  const [showCustomTargets, setShowCustomTargets] = React.useState(false);
  
  // Determine current targets mode
  const targetsMode = aiNutritionGoals?.targets_mode || 'miq';
  const customTargets = aiNutritionGoals?.custom_targets;
  const miqRecommended = aiNutritionGoals?.miq_recommended || aiNutritionGoals?.nutrition_goals?.daily_targets;
  
  // Calculate completion percentage (exact same logic as original)
  // Use actual MIQ confidence if available, otherwise fallback to calculation
  const questionnaireComplete = aiNutritionGoals?.intake_complete ? 30 : 0;
  const hasBodyComp = userProfile?.body_composition?.body_type || metabolicProfile?.somatotype;
  const bodyCompComplete = hasBodyComp ? 25 : 0;
  const mealsLogged = Math.min((todayMeals?.length || 0) * 5, 25); // 5% per meal, max 25%
  const profileComplete = userProfile ? 20 : 0;
  
  // Use actual MIQ confidence from backend if available
  const actualMiqConfidence = aiMiq?.confidence?.overall || aiMiq?.confidence?.percentage;
  const calculatedConfidence = questionnaireComplete + bodyCompComplete + mealsLogged + profileComplete;
  const totalConfidence = actualMiqConfidence ? Math.round(actualMiqConfidence) : calculatedConfidence;
  
  // Get target weight from user profile or intake data (convert if needed)
  const isImperial = userProfile?.use_imperial;
  const rawTargetWeightKg = userProfile?.goal_weight_kg || aiNutritionGoals?.intake_data?.target_weight_kg;
  const rawCurrentWeightKg = userProfile?.weight_kg || aiNutritionGoals?.intake_data?.current_weight_kg;
  
  const targetWeight = rawTargetWeightKg 
    ? (isImperial ? kgToLbs(rawTargetWeightKg) : rawTargetWeightKg)
    : null;
  const currentWeight = rawCurrentWeightKg
    ? (isImperial ? kgToLbs(rawCurrentWeightKg) : rawCurrentWeightKg)
    : null;
  const weightGoalTimeline = aiNutritionGoals?.intake_data?.weight_goal_timeline || 'gradual';
  
  // Determine weight unit based on use_imperial preference (synced with Profile settings)
  const weightUnit = isImperial ? 'lbs' : 'kg';
  
  // Handle retake assessment
  const handleRetakeAssessment = () => {
    if (setAiNutritionGoals) {
      // Reset intake_complete to show the form again
      setAiNutritionGoals(prev => ({
        ...prev,
        intake_complete: false
      }));
    }
    setShowRetakeConfirm(false);
  };
  
  // Handle custom targets update
  const handleTargetsUpdated = async (data) => {
    // Reload nutrition goals to get updated data
    if (loadAiNutritionGoals && userId) {
      await loadAiNutritionGoals(userId);
    }
    setShowCustomTargets(false);
  };

  // Get effective targets to display
  const displayTargets = targetsMode === 'custom' && customTargets 
    ? customTargets 
    : aiNutritionGoals?.nutrition_goals?.daily_targets;

  return (
    <div>
      <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#38ef7d', textAlign: 'center' }}>
        ✅ {aiNutritionGoals.miq_impact}
      </p>
      
      {/* Custom Targets Section (when open) */}
      {showCustomTargets ? (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '14px' }}>🎯 Target Settings</h4>
            <button
              onClick={() => setShowCustomTargets(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ✕
            </button>
          </div>
          <CustomTargetsEditor
            userId={userId}
            currentTargetsMode={targetsMode}
            currentCustomTargets={customTargets}
            miqRecommended={miqRecommended}
            onTargetsUpdated={handleTargetsUpdated}
            onClose={() => setShowCustomTargets(false)}
          />
        </div>
      ) : (
        <>
          {/* Daily Targets */}
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: '14px' }}>Daily Targets</h4>
              <button
                data-testid="customize-targets-btn"
                onClick={() => setShowCustomTargets(true)}
                style={{
                  background: targetsMode === 'custom' 
                    ? 'linear-gradient(135deg, rgba(56, 239, 125, 0.2) 0%, rgba(17, 153, 142, 0.2) 100%)'
                    : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                  border: targetsMode === 'custom'
                    ? '1px solid rgba(56, 239, 125, 0.4)'
                    : '1px solid rgba(102, 126, 234, 0.3)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  color: targetsMode === 'custom' ? '#38ef7d' : '#667eea',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {targetsMode === 'custom' ? '✏️ Edit Custom' : '✏️ Customize'} 
              </button>
            </div>
            
            {/* Target Source Badge */}
            {targetsMode === 'custom' && (
              <div style={{ 
                marginBottom: '12px',
                padding: '8px 10px',
                background: 'rgba(56, 239, 125, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(56, 239, 125, 0.2)',
                fontSize: '11px',
                color: '#38ef7d',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span>✏️</span>
                <span>Target Source: <strong>Custom</strong></span>
                {aiNutritionGoals?.custom_targets_updated_at && (
                  <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '10px' }}>
                    Updated: {new Date(aiNutritionGoals.custom_targets_updated_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Calories</span>
                <p style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{displayTargets?.calories}</p>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Protein</span>
                <p style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{displayTargets?.protein_g}g</p>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Carbs</span>
                <p style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{displayTargets?.carbs_g}g</p>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Fat</span>
                <p style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{displayTargets?.fat_g}g</p>
              </div>
            </div>
          </div>
          
          {/* Weight Goal Section - Shows target weight and timeline for Genius users */}
          {targetWeight && (
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.1) 0%, rgba(0, 184, 148, 0.1) 100%)', 
              borderRadius: '12px', 
              padding: '16px', 
              marginBottom: '16px',
              border: '1px solid rgba(56, 239, 125, 0.2)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <span style={{ fontSize: '16px' }}>🎯</span>
                <h4 style={{ margin: 0, fontSize: '14px', color: '#38ef7d' }}>Weight Goal</h4>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Current</span>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                    {currentWeight ? `${currentWeight} ${weightUnit}` : '—'}
                  </p>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Target</span>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#38ef7d' }}>
                    {targetWeight} {weightUnit}
                  </p>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Timeline</span>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                    {weightGoalTimeline === 'aggressive' ? '🔥 Aggressive' : 
                     weightGoalTimeline === 'moderate' ? '⚡ Moderate' : 
                     weightGoalTimeline === 'gradual' ? '🌱 Gradual' : 
                     weightGoalTimeline === 'maintain' ? '⚖️ Maintain' : '—'}
                  </p>
                </div>
              </div>
              
              {/* Weight change indicator */}
              {currentWeight && targetWeight && currentWeight !== targetWeight && (
                <div style={{ 
                  marginTop: '10px', 
                  padding: '8px 10px', 
                  background: 'rgba(255,255,255,0.05)', 
                  borderRadius: '8px',
                  fontSize: '11px',
                  color: 'var(--text-secondary)'
                }}>
                  {currentWeight > targetWeight 
                    ? `📉 Goal: Lose ${(currentWeight - targetWeight).toFixed(1)} ${weightUnit}`
                    : `📈 Goal: Gain ${(targetWeight - currentWeight).toFixed(1)} ${weightUnit}`
                  }
                  <span style={{ marginLeft: '8px', color: '#38ef7d' }}>
                    • Your daily targets are calibrated for this goal
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Your Profile Summary - Shows ALL SmartGoals selections */}
          {aiNutritionGoals?.intake_data && (
            <div style={{ 
              background: 'rgba(255,255,255,0.03)', 
              borderRadius: '12px', 
              padding: '16px', 
              marginBottom: '16px',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '14px' }}>📋 Your Profile Summary</h4>
                <button
                  onClick={onEditSmartGoals}
                  style={{
                    background: 'rgba(102, 126, 234, 0.15)',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    color: '#667eea',
                    fontSize: '10px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '12px' }}>
                {/* Goal Emphasis */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Goal Emphasis</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.goal_emphasis?.replace(/_/g, ' ') || 'N/A'}
                  </span>
                </div>
                
                {/* Target Weight */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Target Weight</span>
                  <span style={{ color: 'var(--text-primary)' }}>
                    {targetWeight ? `${targetWeight} ${weightUnit}` : 'N/A'}
                  </span>
                </div>
                
                {/* Timeline */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Timeline</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.goal_timeline_weeks 
                      ? `${aiNutritionGoals.intake_data.goal_timeline_weeks} weeks`
                      : aiNutritionGoals.intake_data.weight_goal_timeline?.replace(/_/g, ' ') || 'N/A'}
                  </span>
                </div>
                
                {/* Meals Per Day */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Meals Per Day</span>
                  <span style={{ color: 'var(--text-primary)' }}>
                    {aiNutritionGoals.intake_data.meals_per_day === 'custom' && aiNutritionGoals.intake_data.custom_meals_count
                      ? aiNutritionGoals.intake_data.custom_meals_count
                      : aiNutritionGoals.intake_data.meals_per_day || 'N/A'}
                  </span>
                </div>
                
                {/* Training vs Rest Days */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Training/Rest Day Targets</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.training_rest_difference === 'yes' 
                      ? 'Different targets' 
                      : aiNutritionGoals.intake_data.training_rest_difference === 'no' 
                        ? 'Same targets' 
                        : 'N/A'}
                  </span>
                </div>
                
                {/* Flexible Macro Mode */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Flexible Macro Mode</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.flexible_mode === 'enabled' 
                      ? 'Enabled' 
                      : aiNutritionGoals.intake_data.flexible_mode === 'disabled' 
                        ? 'Disabled' 
                        : 'N/A'}
                  </span>
                </div>
                
                {/* Sleep Quality */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Sleep Quality</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.sleep_quality?.replace(/_/g, ' ') || 'N/A'}
                  </span>
                </div>
                
                {/* Stress Level */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Stress Level</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.stress_level?.replace(/_/g, ' ') || 'N/A'}
                  </span>
                </div>
                
                {/* Activity Style */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Activity Style</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.activity_style?.replace(/_/g, ' ') || 'N/A'}
                  </span>
                </div>
                
                {/* Body Type */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Body Type</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.body_type?.replace(/_/g, ' ') || 'N/A'}
                  </span>
                </div>
                
                {/* Work Schedule */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Work Schedule</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.work_schedule_type?.replace(/_/g, ' ') || 'N/A'}
                  </span>
                </div>
                
                {/* Ancestry */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Ancestry</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.ancestry?.replace(/_/g, ' ') || 'N/A'}
                  </span>
                </div>
                
                {/* Diet Type */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Diet Type</span>
                  <span style={{ color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                    {aiNutritionGoals.intake_data.dietary_preference && aiNutritionGoals.intake_data.dietary_preference !== 'none'
                      ? aiNutritionGoals.intake_data.dietary_preference.replace(/_/g, ' ')
                      : 'N/A'}
                  </span>
                </div>
                
                {/* IF Protocol */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Fasting Protocol</span>
                  <span style={{ color: 'var(--text-primary)' }}>
                    {aiNutritionGoals.intake_data.if_preference && aiNutritionGoals.intake_data.if_preference !== 'none'
                      ? aiNutritionGoals.intake_data.if_preference.replace('_', ':')
                      : 'N/A'}
                  </span>
                </div>
                
                {/* Eating Window Start */}
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '10px', display: 'block' }}>Eating Window Start</span>
                  <span style={{ color: 'var(--text-primary)' }}>
                    {aiNutritionGoals.intake_data.eating_window_start || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Health Profile Section - Shows medical conditions and allergies */}
      {(aiNutritionGoals.intake_data?.medical_conditions?.filter(c => c !== 'none').length > 0 || 
        aiNutritionGoals.intake_data?.food_allergies?.filter(a => a !== 'none').length > 0) && (
        <div style={{ 
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(249, 115, 22, 0.08) 100%)', 
          borderRadius: '12px', 
          padding: '16px', 
          marginBottom: '16px',
          border: '1px solid rgba(239, 68, 68, 0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <span style={{ fontSize: '16px' }}>🏥</span>
            <h4 style={{ margin: 0, fontSize: '14px', color: '#f87171' }}>Health Profile Active</h4>
          </div>
          
          <p style={{ margin: '0 0 10px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
            AI recommendations consider your health conditions
          </p>
          
          {/* Medical Conditions */}
          {aiNutritionGoals.intake_data?.medical_conditions?.filter(c => c !== 'none').length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                Medical Conditions:
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {aiNutritionGoals.intake_data.medical_conditions.filter(c => c !== 'none').map((condition, i) => (
                  <span key={i} style={{
                    background: 'rgba(239, 68, 68, 0.15)',
                    color: '#f87171',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '500',
                    textTransform: 'capitalize'
                  }}>
                    {condition.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Food Allergies */}
          {aiNutritionGoals.intake_data?.food_allergies?.filter(a => a !== 'none').length > 0 && (
            <div>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                Food Allergies:
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {aiNutritionGoals.intake_data.food_allergies.filter(a => a !== 'none').map((allergy, i) => (
                  <span key={i} style={{
                    background: 'rgba(249, 115, 22, 0.15)',
                    color: '#fb923c',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '500',
                    textTransform: 'capitalize'
                  }}>
                    {allergy.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* MIQ Confidence Progress */}
      <div style={{ 
        marginTop: '20px', 
        padding: '16px', 
        background: 'linear-gradient(135deg, rgba(56, 239, 125, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(56, 239, 125, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h4 style={{ margin: 0, fontSize: '14px', color: '#38ef7d' }}>
            🎯 MIQ Confidence Analysis
          </h4>
          <span style={{ 
            fontSize: '18px', 
            fontWeight: '700', 
            color: totalConfidence >= 75 ? '#38ef7d' : totalConfidence >= 50 ? '#667eea' : '#ff9800'
          }}>
            {totalConfidence}%
          </span>
        </div>
        
        {/* Progress Bar */}
        <div style={{ 
          height: '8px', 
          background: 'rgba(255,255,255,0.1)', 
          borderRadius: '4px',
          overflow: 'hidden',
          marginBottom: '12px'
        }}>
          <div style={{ 
            width: `${totalConfidence}%`, 
            height: '100%', 
            background: totalConfidence >= 75 ? 'linear-gradient(90deg, #38ef7d, #11998e)' : 
                       totalConfidence >= 50 ? 'linear-gradient(90deg, #667eea, #764ba2)' : 
                       'linear-gradient(90deg, #ff9800, #f44336)',
            borderRadius: '4px',
            transition: 'width 0.5s ease'
          }} />
        </div>
        
        <p style={{ margin: '0 0 12px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
          You&apos;ve completed <strong style={{ color: 'var(--text-primary)' }}>{totalConfidence}%</strong> of your MIQ Confidence analysis. 
          Higher confidence = more accurate AI recommendations.
        </p>
        
        {/* Completion Checklist */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
            <span style={{ color: profileComplete ? '#38ef7d' : 'var(--text-secondary)' }}>
              {profileComplete ? '✅' : '○'}
            </span>
            <span style={{ color: profileComplete ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              Profile Setup (+20%)
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
            <span style={{ color: questionnaireComplete ? '#38ef7d' : 'var(--text-secondary)' }}>
              {questionnaireComplete ? '✅' : '○'}
            </span>
            <span style={{ color: questionnaireComplete ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              MIQ Assessment (+30%)
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
            <span style={{ color: hasBodyComp ? '#38ef7d' : 'var(--text-secondary)' }}>
              {hasBodyComp ? '✅' : '○'}
            </span>
            <span style={{ color: hasBodyComp ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              Metabolic Profile (+25%)
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
            <span style={{ color: mealsLogged >= 25 ? '#38ef7d' : mealsLogged > 0 ? '#667eea' : 'var(--text-secondary)' }}>
              {mealsLogged >= 25 ? '✅' : mealsLogged > 0 ? '◐' : '○'}
            </span>
            <span style={{ color: mealsLogged > 0 ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              Meal Logging (+{mealsLogged}% of 25%)
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
            <span style={{ color: checkedInToday ? '#38ef7d' : dailyCheckinContribution?.total_checkins > 0 ? '#667eea' : 'var(--text-secondary)' }}>
              {dailyCheckinContribution?.confidence_boost >= 15 ? '✅' : dailyCheckinContribution?.total_checkins > 0 ? '◐' : '○'}
            </span>
            <span style={{ color: dailyCheckinContribution?.total_checkins > 0 ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              Daily Check-Ins (+{dailyCheckinContribution?.confidence_boost || 0}% of 15%)
            </span>
          </div>
        </div>
        
        {/* Choice: Continue Building or Explore */}
        <p style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
          What would you like to do next?
        </p>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => {
              setShowAiNutritionGoals(false);
              // Open next incomplete step in the MIQ journey
              if ((todayMeals?.length || 0) === 0) {
                setShowMealPlanner(true);
              } else if (mealsLogged < 25) {
                setShowDailyCheckin(true);
              } else {
                setShowMealPlanner(true);
              }
            }}
            style={{
              flex: 1,
              padding: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            🚀 Keep Building MIQ
          </button>
          <button
            onClick={() => setShowAiNutritionGoals(false)}
            style={{
              flex: 1,
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            🔍 Explore Features
          </button>
        </div>
        
        {/* Wearable tip */}
        <p style={{ margin: '12px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          💡 Pro tip: Reference data from wearables (sleep, activity) when logging meals to boost accuracy even further.
        </p>
      </div>
      
      {/* Update Preferences Button - Now opens edit mode directly */}
      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <button
          data-testid="edit-smartgoals-btn"
          onClick={() => onEditSmartGoals && onEditSmartGoals()}
          style={{
            padding: '10px 20px',
            background: 'rgba(102, 126, 234, 0.1)',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            borderRadius: '8px',
            color: '#667eea',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            margin: '0 auto'
          }}
        >
          ✏️ Edit SmartGoals
        </button>
        <p style={{ margin: '8px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
          Change any field — your previous answers are saved
        </p>
      </div>
      
      {/* Retake Confirmation Modal */}
      {showRetakeConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-primary)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '340px',
            width: '100%',
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '40px', display: 'block', marginBottom: '12px' }}>🔄</span>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
              Update Your Preferences?
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              This will let you retake the SmartGoals assessment to update your:
            </p>
            <ul style={{ 
              textAlign: 'left', 
              margin: '0 0 20px 0', 
              padding: '0 0 0 24px',
              fontSize: '12px',
              color: 'var(--text-secondary)',
              lineHeight: 1.8
            }}>
              <li>Goal emphasis (fat loss, muscle gain, etc.)</li>
              <li>Intermittent fasting preferences</li>
              <li>Sleep & stress levels</li>
              <li>Activity style</li>
              <li><strong style={{ color: '#667eea' }}>Ancestry / ethnic background</strong></li>
            </ul>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowRetakeConfirm(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                data-testid="confirm-retake-btn"
                onClick={handleRetakeAssessment}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Update Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AINutritionGoalsModal;
